import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventsApi } from '../utils/api';
import { IconBroadcast, IconCalendar, IconTrophy } from '../components/Icons';
import { eventStatusLabel } from '../utils/eventStatus';

export default function AllEvents() {
  const { isOrganizer } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventsApi.getEvents()
      .then(res => setEvents(res.data.data || []))
      .catch(err => console.error('Failed to fetch events:', err))
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();

  // Statuses visible to all users (backend already filters to these for participants)
  const visibleStatuses = ['active', 'live', 'pending_payment'];
  // Draft is only relevant for organizer-owned events
  const draftStatuses = ['draft'];

  const current = events.filter(ev => {
    if (ev.status === 'live') return true;
    const start = new Date(ev.start_date);
    const end = new Date(ev.end_date);
    const withinDates = start <= now && now <= end;
    if (visibleStatuses.includes(ev.status) && withinDates) return true;
    if (isOrganizer && draftStatuses.includes(ev.status) && withinDates) return true;
    return false;
  });

  const upcoming = events.filter(ev => {
    const start = new Date(ev.start_date);
    if (start <= now) return false;
    if (visibleStatuses.includes(ev.status)) return true;
    if (isOrganizer && draftStatuses.includes(ev.status)) return true;
    return false;
  });

  const past = events.filter(ev => {
    if (ev.status === 'completed') return true;
    if (ev.status === 'cancelled') return true;
    // Events whose end date has passed
    const end = new Date(ev.end_date);
    if (end < now && visibleStatuses.includes(ev.status)) return true;
    if (end < now && isOrganizer && draftStatuses.includes(ev.status)) return true;
    return false;
  });

  if (loading) return <div className="spinner" />;

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <h1>All Events</h1>
          <p style={{ color: '#8a9bb5' }}>Browse current, upcoming, and past fishing events</p>
        </div>

        <EventSection title="Current Events" icon={<IconBroadcast size="16px" />} events={current} emptyText="No events happening right now" />
        <EventSection title="Upcoming Events" icon={<IconCalendar size="16px" />} events={upcoming} emptyText="No upcoming events scheduled" />
        <EventSection title="Past Events" icon={<IconTrophy size="16px" />} events={past} emptyText="No past events" />
      </div>
    </div>
  );
}

function EventSection({ title, icon, events, emptyText }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>{icon}{title}</h3>
        <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{events.length} event{events.length !== 1 ? 's' : ''}</span>
      </div>
      {events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: '#8a9bb5' }}>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {events.map(ev => (
            <EventCard key={ev.id} event={ev} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ event }) {
  const navigate = useNavigate();
  const startDate = new Date(event.start_date).toLocaleDateString();
  const endDate = new Date(event.end_date).toLocaleDateString();
  const dateRange = startDate === endDate ? startDate : `${startDate} – ${endDate}`;

  return (
    <Link
      to={`/events/${event.id}/overview`}
      className="event-card-row"
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px', background: '#1e2d3d', borderRadius: 10,
        border: '1px solid #2a3f56', textDecoration: 'none',
        transition: 'all 0.2s'
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#e8a838'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3f56'}
    >
      <div>
        <div style={{ color: '#f0f2f5', fontWeight: 500, marginBottom: 2 }}>{event.name}</div>
        <div style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>
          {event.location || 'No location'} · {dateRange}
        </div>
        {event.organizer_name && (
          <div style={{ color: '#6b7f99', fontSize: '0.75rem', marginTop: 2 }}>
            by {event.organizer_name}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {event.event_type && (
          <span className="badge badge-active" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
            {event.event_type}
          </span>
        )}
        {event.status === 'pending_payment' ? (
          <button
            className="btn btn-warning btn-sm"
            style={{ padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600 }}
            onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/events/${event.id}/pay`); }}
          >
            Complete Payment
          </button>
        ) : (
          <span className={`badge badge-${event.status}`}>{eventStatusLabel(event.status)}</span>
        )}
      </div>
    </Link>
  );
}
